import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyService } from '../../services/property.service';
import { NavigationContextService } from '../../services/navigation-context.service';

@Component({
  selector: 'app-edit-property',
  templateUrl: './edit-property.component.html',
  styleUrls: ['./edit-property.component.scss']
})
export class EditPropertyComponent implements OnInit {
  propertyForm: FormGroup;
  property: any;
  attributes: any[] = [];
  selectedFile: File | null = null;
  previewImage: string | ArrayBuffer | null = null;
  originalImageUrl: string | null = null;
  loading = true;
  saving = false;
  archiving = false;
  error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private route: ActivatedRoute,
    private router: Router,
    private navContext: NavigationContextService
  ) {
    this.propertyForm = this.fb.group({
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', Validators.required],
      monthlyCost: ['', [Validators.required, Validators.min(0)]],
      managed: [false],
      coverImage: ['']
    });
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = 'No property specified.';
      this.loading = false;
      return;
    }
    Promise.all([
      this.propertyService.getPublicProperty(id),
      this.propertyService.getAdminAttributes()
    ]).then(([prop, attrs]) => {
      this.property = prop;
      this.attributes = attrs || [];
      this.loading = false;
      this.buildForm();
    }).catch(err => {
      console.error('Error loading property:', err);
      this.error = err && err.message ? err.message : 'Failed to load property.';
      this.loading = false;
    });
  }

  private buildForm() {
    const p = this.property;
    if (!p) return;
    this.propertyForm.patchValue({
      addressLine1: p.addressLine1 || '',
      addressLine2: p.addressLine2 || '',
      city: p.city || '',
      state: p.state || '',
      zipCode: p.zipCode || '',
      monthlyCost: p.monthlyCost != null ? p.monthlyCost : '',
      managed: !!p.managed,
      coverImage: p.coverImage || ''
    });
    this.attributes.forEach(attr => {
      const key = `attr_${attr.id}`;
      if (!this.propertyForm.get(key)) {
        this.propertyForm.addControl(key, this.fb.control(''));
      }
      let value = '';
      if (p.attributes && Array.isArray(p.attributes)) {
        const av = p.attributes.find((a: any) => a.attributeId === attr.id);
        value = (av && av.value) ? av.value : '';
      }
      this.propertyForm.get(key)!.setValue(value);
    });
    if (p.coverImage) {
      this.originalImageUrl = p.coverImage;
      this.previewImage = p.coverImage.startsWith('http') || p.coverImage.startsWith('data:')
        ? p.coverImage
        : p.coverImage;
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const res = e.target && e.target.result;
        this.previewImage = res || null;
        this.propertyForm.patchValue({ coverImage: this.previewImage });
      };
      reader.readAsDataURL(file);
    }
  }

  handleAddressChange(address: any) {
    let street = '';
    let streetNumber = '';
    let line2 = '';
    for (let i = 0; i < address.address_components.length; i++) {
      const c = address.address_components[i];
      if (c.types.includes('administrative_area_level_1')) this.propertyForm.patchValue({ state: c.short_name });
      if (c.types.includes('street_number')) streetNumber = c.short_name;
      if (c.types.includes('route')) street = c.short_name;
      if (c.types.includes('subpremise')) line2 = c.short_name;
      if (c.types.includes('locality')) this.propertyForm.patchValue({ city: c.short_name });
      if (c.types.includes('postal_code')) this.propertyForm.patchValue({ zipCode: c.short_name });
    }
    this.propertyForm.patchValue({
      addressLine1: (streetNumber + ' ' + street).trim(),
      addressLine2: line2 || ''
    });
  }

  onCancel() {
    if (this.property && this.property.id) {
      this.navContext.goToPropertyHub(this.property.id);
    } else {
      this.navContext.goToDashboard();
    }
  }

  private returnToProperty() {
    if (this.property && this.property.id) {
      this.navContext.goToPropertyHub(this.property.id);
    } else {
      this.navContext.goToDashboard();
    }
  }

  onSubmit() {
    if (!this.propertyForm.valid || !this.property || !this.property.id) return;
    this.saving = true;
    const formValue = this.propertyForm.value;
    const property: any = {
      id: this.property.id,
      addressLine1: formValue.addressLine1,
      addressLine2: formValue.addressLine2 || null,
      city: formValue.city,
      state: formValue.state,
      zipCode: formValue.zipCode,
      monthlyCost: parseFloat(formValue.monthlyCost) || 0,
      managed: !!formValue.managed,
      coverImage: this.selectedFile ? '' : (this.originalImageUrl || ''),
      attributes: this.attributes.map(attr => {
        const formControl = this.propertyForm.get(`attr_${attr.id}`);
        const rawValue = formControl ? formControl.value : null;
        const value = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
        return { attributeId: attr.id, value };
      })
    };
    this.propertyService.updateProperty(property).then(() => {
      if (this.selectedFile) {
        this.propertyService.uploadCoverImage(this.property.id, this.selectedFile!).then(() => {
          this.propertyService.clearProperties();
          this.saving = false;
          this.returnToProperty();
        }).catch(uploadErr => {
          console.error('Error uploading image:', uploadErr);
          this.propertyService.clearProperties();
          this.saving = false;
          this.returnToProperty();
        });
      } else {
        this.propertyService.clearProperties();
        this.saving = false;
        this.returnToProperty();
      }
    }).catch(err => {
      console.error('Error updating property:', err);
      this.saving = false;
    });
  }

  get isArchived(): boolean {
    return !!(this.property && this.property.archivedAt);
  }

  get hasActiveLease(): boolean {
    return !!(this.property && this.property.status === 'LEASED');
  }

  /**
   * Soft-archive the property: hides it from operational lists but keeps all
   * statements, expenses, invoices, and past leases so reports and tax
   * exports still see it. Refuses if a lease is active (mirrors backend).
   */
  onArchive() {
    if (!this.property || !this.property.id || this.archiving) return;
    if (this.hasActiveLease) {
      alert('This property still has an active lease. End the lease before archiving.');
      return;
    }
    const label = this.property.addressLine1 || 'this property';
    const confirmed = confirm(
      `Archive ${label}?\n\nIt will disappear from the dashboard, marketing site, and pickers. ` +
      `All history (statements, expenses, invoices, past leases) is preserved and it can be restored ` +
      `from the Archived Properties page.`
    );
    if (!confirmed) return;
    this.archiving = true;
    this.propertyService.archiveProperty(this.property).then(() => {
      this.propertyService.clearProperties();
      this.archiving = false;
      this.router.navigate(['/archived-properties']);
    }).catch(err => {
      console.error('Error archiving property:', err);
      const message = (err && err.error && err.error.error) || 'Failed to archive property.';
      this.archiving = false;
      alert(message);
    });
  }

  /** Restore an archived property back to the dashboard. */
  onUnarchive() {
    if (!this.property || !this.property.id || this.archiving) return;
    this.archiving = true;
    this.propertyService.unarchiveProperty(this.property).then((restored: any) => {
      if (restored) {
        this.property = { ...this.property, ...restored };
      } else {
        this.property = { ...this.property, archivedAt: null, archivedBy: null };
      }
      this.propertyService.clearProperties();
      this.archiving = false;
    }).catch(err => {
      console.error('Error restoring property:', err);
      const message = (err && err.error && err.error.error) || 'Failed to restore property.';
      this.archiving = false;
      alert(message);
    });
  }

  // Map an attribute's declared type to an HTML input type.
  inputTypeFor(attr: any): string {
    const type = (attr && attr.attributeType ? String(attr.attributeType) : '').toLowerCase();
    if (type === 'number') {
      return 'number';
    }
    if (type === 'date') {
      return 'date';
    }
    return 'text';
  }
}
